import { Link, useParams } from 'react-router-dom';
import { useApi } from '../api';
import { Card, ErrorBanner, Loading, StatTile } from '../components/ui';
import { useT } from '../i18n';

/**
 * Cómo le va a una influencer.
 *
 * ── Lo que no se puede, dicho una vez ───────────────────────────────────────────
 * «Qué seguidores compartimos con un referente» no se puede calcular: la API de Instagram no
 * expone la lista de seguidores de nadie, ni siquiera la propia. No es una limitación de
 * permisos que se arregle con una revisión de app — ese endpoint no existe.
 *
 * Lo que sí se puede es comparar **de dónde** es la audiencia con de dónde son las cuentas que
 * se miran, y eso contesta la pregunta de atrás: si te seguís pareciendo a lo que mirás.
 *
 * ── Por qué los gráficos empiezan vacíos ────────────────────────────────────────
 * Meta no deja pedir el pasado. La serie arranca el día que se empezó a capturar, y no hay
 * forma de recuperar lo anterior. La pantalla lo dice en vez de mostrar una línea plana que
 * parece un dato.
 */

type Punto = {
  dayUtc: string;
  followers: number | null;
  reach: number | null;
  views: number | null;
  interactions: number | null;
};

type Metricas = {
  username: string | null;
  followers: number | null;
  follows: number | null;
  mediaCount: number | null;
  views: number | null;
  reach: number | null;
  interactions: number | null;
  accountsEngaged: number | null;
};

type Estadisticas = {
  creator: { id: string; publicName: string; niche: string[]; countryNarrative: string | null };
  metrics: Metricas | null;
  metricsFacebook: Partial<Metricas> | null;
  /** La suma de las dos, calculada en el servidor. `null` en un campo es «ninguna lo informa». */
  metricsTotal: Omit<Metricas, 'username'> | null;
  serie: Punto[];
  serieFacebook: Punto[];
  referenciasPorPais: { pais: string; cuantas: number; seguidores: number }[];
};

const nada = '—';

function formatear(valor: number | null | undefined): string {
  if (valor === null || valor === undefined) return nada;
  if (valor >= 1_000_000) return `${(valor / 1_000_000).toFixed(1)}M`;
  if (valor >= 1_000) return `${(valor / 1_000).toFixed(1)}K`;
  return String(valor);
}

/**
 * Las dos series en una, alineadas por día.
 *
 * Cada plataforma se mide por separado y puede fallar por separado, así que los días no
 * coinciden: una cuenta nueva de Facebook empieza a medirse semanas después que la de Instagram.
 * Se recorren todos los días que aparezcan en cualquiera de las dos y se suma lo que haya.
 *
 * `null` sigue siendo «no informado» y no cero: si ninguna de las dos midió un campo ese día, el
 * punto queda en `null` y el gráfico dibuja un hueco. Rellenarlo con cero haría un pozo en la
 * línea de seguidores que se leería como una caída real.
 */
function sumarSeries(a: Punto[], b: Punto[]): Punto[] {
  const dias = [...new Set([...a, ...b].map((punto) => punto.dayUtc))].sort();
  const porDia = (serie: Punto[]) => new Map(serie.map((punto) => [punto.dayUtc, punto]));
  const [mapaA, mapaB] = [porDia(a), porDia(b)];

  const sumar = (uno: Punto | undefined, otro: Punto | undefined, campo: keyof Punto) => {
    const valores = [uno?.[campo], otro?.[campo]].filter(
      (valor): valor is number => typeof valor === 'number',
    );
    return valores.length === 0 ? null : valores.reduce((total, valor) => total + valor, 0);
  };

  return dias.map((dayUtc) => {
    const uno = mapaA.get(dayUtc);
    const otro = mapaB.get(dayUtc);
    return {
      dayUtc,
      followers: sumar(uno, otro, 'followers'),
      reach: sumar(uno, otro, 'reach'),
      views: sumar(uno, otro, 'views'),
      interactions: sumar(uno, otro, 'interactions'),
    };
  });
}

export function Estadisticas() {
  const { t } = useT();
  const { id } = useParams<{ id: string }>();
  const state = useApi<Estadisticas>(id ? `/api/creators/${id}/estadisticas` : null);

  if (state.loading && !state.data) return <Loading label={t('stats.leyendo')} />;
  if (state.error) return <ErrorBanner error={state.error} onRetry={state.reload} />;
  if (!state.data) return null;

  const { creator, metrics, metricsFacebook, metricsTotal, serie, serieFacebook } = state.data;
  const { referenciasPorPais } = state.data;
  const totalReferencias = referenciasPorPais.reduce((suma, fila) => suma + fila.cuantas, 0);

  /*
    El desglose que va debajo de cada total.

    Se muestra siempre que haya una segunda plataforma conectada, aunque el campo venga vacío en
    una: «IG 40 · FB —» dice algo que «40» solo no dice, que es de dónde salen los cuarenta.
  */
  const sumada = sumarSeries(serie, serieFacebook);
  const hayDos = metrics !== null && metricsFacebook !== null;
  const desglose = (clave: keyof Omit<Metricas, 'username'>) =>
    hayDos
      ? `IG ${formatear(metrics?.[clave])} · FB ${formatear(metricsFacebook?.[clave])}`
      : undefined;

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/plataformas"
          className="text-xs text-[var(--color-ink-muted)] hover:text-[var(--color-accent)]"
        >
          {t('stats.volverPlataformas')}
        </Link>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight">{creator.publicName}</h1>
        <p className="mt-1 text-sm text-[var(--color-ink-muted)]">
          {creator.niche.join(' · ')}
          {creator.countryNarrative ? ` · ${creator.countryNarrative}` : ''}
          {metrics?.username ? ` · @${metrics.username}` : ''}
        </p>
      </div>

      {/*
        La lectura de hoy, en vivo.

        No se cachea a propósito: un número guardado envejece sin avisar, y acá dice «ahora».
        Si la cuenta no está conectada o todavía no tiene actividad, viene en null y se muestra
        un guion — nunca un cero, que sería un dato y sería falso.
      */}
      {metricsTotal === null ? (
        <div className="rounded-2xl border border-[var(--color-border-subtle)] bg-[var(--color-surface-sunken)] p-4 text-sm text-[var(--color-ink-muted)]">
          <strong className="text-[var(--color-ink)]">{t('stats.sinMetricasTitulo')}</strong>{' '}
          {t('stats.sinMetricasDetalle')}
        </div>
      ) : (
        // Dos columnas ya en el teléfono: son cifras cortas, y en una sola columna cada una
        // ocupaba media pantalla de alto.
        <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <StatTile
            label={t('stats.seguidores')}
            value={formatear(metricsTotal.followers)}
            hint={desglose('followers')}
          />
          <StatTile
            label={t('stats.seguidos')}
            value={formatear(metricsTotal.follows)}
            hint={desglose('follows')}
          />
          <StatTile
            label={t('stats.publicaciones')}
            value={formatear(metricsTotal.mediaCount)}
            hint={desglose('mediaCount')}
          />
          <StatTile
            label={t('stats.ratioTitulo')}
            value={
              /*
                Sobre el total, y sólo cuando los dos números existen.

                `metricsTotal.follows` puede ser sólo el de Instagram —Facebook no informa a
                cuántas cuentas sigue una Página—, así que este ratio mezcla seguidores de dos
                plataformas con seguidos de una. Se aclara en el pie en vez de esconderlo: el
                número igual sirve para ver la tendencia.
              */
              metricsTotal.followers && metricsTotal.follows
                ? (metricsTotal.followers / metricsTotal.follows).toFixed(1)
                : nada
            }
            hint={t('stats.ratio')}
          />
          <StatTile
            label={t('stats.alcance')}
            value={formatear(metricsTotal.reach)}
            hint={desglose('reach') ?? t('stats.ultimos7')}
          />
          <StatTile
            label={t('stats.vistas')}
            value={formatear(metricsTotal.views)}
            hint={desglose('views') ?? t('stats.ultimos7')}
          />
          <StatTile
            label={t('stats.interacciones')}
            value={formatear(metricsTotal.interactions)}
            hint={desglose('interactions') ?? t('stats.ultimos7')}
          />
          <StatTile
            label={t('stats.cuentasAlcanzadas')}
            value={formatear(metricsTotal.accountsEngaged)}
            hint={desglose('accountsEngaged') ?? t('stats.ultimos7')}
          />
        </div>
      )}

      {hayDos && (
        <p className="-mt-3 text-xs text-[var(--color-ink-muted)]">
          {t('stats.totalesSuman')}
        </p>
      )}

      <Card title={t('stats.evolucion')}>
        {sumada.length < 2 ? (
          /*
            Con menos de dos puntos no hay línea que dibujar, y dibujar una plana mentiría.
            Se dice qué falta y desde cuándo, que es lo único accionable.
          */
          <p className="text-sm text-[var(--color-ink-muted)]">
            {sumada.length === 0
              ? t('stats.sinMediciones')
              : t('stats.unaMedicion')}
            <br />
            <span className="text-xs">
              {t('stats.pasadoNoVuelve')}
            </span>
          </p>
        ) : (
          <div className="space-y-6">
            <Grafico serie={sumada} campo="followers" titulo={t('stats.seguidores')} />
            <Grafico serie={sumada} campo="reach" titulo={t('stats.alcance')} />
            <Grafico serie={sumada} campo="interactions" titulo={t('stats.interacciones')} />
            {hayDos && (
              <p className="text-xs text-[var(--color-ink-muted)]">
                {t('stats.lineasSuman')}
              </p>
            )}
          </div>
        )}
      </Card>

      {/*
        Las referencias por país.

        Es lo más cerca que se puede estar de «qué seguidores compartimos»: ese dato no existe
        en ninguna API de Instagram. Comparar de dónde es lo que mirás sí se puede, y dice algo
        parecido — si tu referencia está en el mismo mercado en el que querés crecer.
      */}
      <Card title={t('stats.referencias', { n: totalReferencias })}>
        {referenciasPorPais.length === 0 ? (
          <p className="text-sm text-[var(--color-ink-muted)]">
            {t('stats.sinReferencias')}{' '}
            <Link to="/referencias" className="underline underline-offset-2">
              {t('stats.agregalas')}
            </Link>
          </p>
        ) : (
          <>
            <p className="mb-3 text-xs text-[var(--color-ink-muted)]">
              {t('stats.deDondeSon')}
            </p>
            <ul className="space-y-2">
              {referenciasPorPais.map((fila) => (
                <li key={fila.pais} className="flex items-center gap-3 text-sm">
                  <span className="w-32 shrink-0 truncate">{fila.pais}</span>
                  <span
                    aria-hidden="true"
                    className="h-2 rounded-full bg-[var(--color-accent)]"
                    style={{ width: `${(fila.cuantas / totalReferencias) * 60}%`, minWidth: 4 }}
                  />
                  <span className="tabular-nums text-[var(--color-ink-muted)]">{fila.cuantas}</span>
                  {fila.seguidores > 0 && (
                    <span className="text-xs text-[var(--color-ink-faint)]">
                      {formatear(fila.seguidores)} {t('stats.seg')}
                    </span>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </Card>
    </div>
  );
}

/**
 * Una línea, en SVG y sin librería.
 *
 * Traer una de gráficos por tres líneas pesaría más que toda la hoja de estilos, y el resto del
 * panel ya dibuja sus iconos a mano por el mismo motivo.
 *
 * Los puntos sin dato **cortan la línea** en vez de valer cero. Un día que no se pudo medir no
 * es un día en cero, y unirlos dibujaría un desplome que nunca ocurrió.
 */
function Grafico({
  serie,
  campo,
  titulo,
}: {
  serie: Punto[];
  campo: 'followers' | 'reach' | 'views' | 'interactions';
  titulo: string;
}) {
  const puntos = serie.map((p) => p[campo]);
  const conDato = puntos.filter((v): v is number => v !== null);
  if (conDato.length < 2) return null;

  const max = Math.max(...conDato);
  const min = Math.min(...conDato);
  const rango = max - min || 1;
  const ancho = 100;
  const alto = 30;

  // Tramos continuos: cada corte por un dato faltante empieza un `path` nuevo.
  const tramos: string[] = [];
  let actual: string[] = [];
  puntos.forEach((valor, i) => {
    if (valor === null) {
      if (actual.length > 1) tramos.push(actual.join(' '));
      actual = [];
      return;
    }
    const x = (i / Math.max(1, puntos.length - 1)) * ancho;
    const y = alto - ((valor - min) / rango) * alto;
    actual.push(`${actual.length === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`);
  });
  if (actual.length > 1) tramos.push(actual.join(' '));

  const ultimo = conDato.at(-1) ?? 0;
  const primero = conDato[0] ?? 0;
  const cambio = ultimo - primero;

  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-xs font-medium">{titulo}</span>
        <span className="text-xs tabular-nums text-[var(--color-ink-muted)]">
          {formatear(ultimo)}
          {cambio !== 0 && (
            <span
              className={
                cambio > 0 ? 'ml-2 text-[var(--color-ok)]' : 'ml-2 text-[var(--color-danger)]'
              }
            >
              {cambio > 0 ? '+' : ''}
              {formatear(cambio)}
            </span>
          )}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        preserveAspectRatio="none"
        className="mt-1 h-16 w-full"
        // Decorativo: el número y el cambio ya están en texto arriba.
        aria-hidden="true"
      >
        {tramos.map((d) => (
          <path
            key={d.slice(0, 24)}
            d={d}
            fill="none"
            stroke="var(--color-accent)"
            strokeWidth={1}
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>
      <div className="flex justify-between text-[0.65rem] text-[var(--color-ink-faint)]">
        <span>{serie[0]?.dayUtc}</span>
        <span>{serie.at(-1)?.dayUtc}</span>
      </div>
    </div>
  );
}
